import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import { requireId } from '@modules/aawz-intelligence-v2/api/guardas'

const MSG_ERRO_TRANSCRICAO =
  'Não foi possível transcrever o áudio. Tente novamente.'
const MSG_ERRO_SINTESE = 'Não foi possível gerar a resposta em voz agora.'
const MSG_ERRO_CATALOGO = 'Não foi possível carregar as vozes disponíveis.'
const MSG_ERRO_AMOSTRA = 'Não foi possível tocar a amostra desta voz agora.'

// [spec-008 T023] As duas mensagens do canal em tempo real. Como todas as
// outras deste arquivo, trazem a PRÓXIMA AÇÃO junto do erro (regra "Mensagens
// de Erro: Sempre Incluem a Solução") — e nenhuma delas nomeia provedor,
// chave ou status HTTP (N7).
const MSG_ERRO_ABRIR_SESSAO =
  'Não foi possível abrir a conversa por voz agora. Tente novamente em instantes.'
// A menção ao encerramento sozinho não é consolo: o registro de posse da
// sessão tem TTL no servidor (`voz_tempo_real_sessao.TTL_SESSAO_SEGUNDOS`) e
// a sessão no provedor tem teto próprio (`teto_segundos`) — falhar aqui
// atrasa a CONFERÊNCIA do consumo, não deixa uma sessão paga viva para
// sempre.
const MSG_ERRO_ENCERRAR_SESSAO =
  'Não foi possível registrar o fim da conversa por voz. A sessão encerra sozinha em alguns minutos.'
const MSG_SEGUNDOS_INVALIDOS =
  'Não foi possível registrar o fim da conversa por voz: a duração medida é inválida. Recarregue a página e tente de novo.'

// [spec-019 T002, ADR-019-03] Prazo de UMA síntese de FRASE — não de uma
// resposta inteira. Sem `timeout`, `con.post` (axios) espera indefinidamente;
// se `POST /voz/sintetizar` pendurar (rede, fila do provedor, gateway), o
// consumidor da fila de fala (`utils/fala-da-ia.js::_consumirFilaDeFala`)
// fica bloqueado pra sempre e o microfone nunca reabre — a sessão de voz
// fica viva e surda (D2 da spec-019). O prazo mora AQUI, na camada de API —
// nunca como um `Promise.race` na fila, que deixaria a requisição viva por
// trás. 12s: o ElevenLabs sintetiza uma frase curta em ~1-2s em operação
// normal (medido — ver CLAUDE.md do módulo, §"Entrada e saída por voz");
// 12s dá margem generosa pra jitter de rede/fila do provedor sem deixar o
// silêncio se estender muito além disso quando algo realmente pendura.
const PRAZO_MS_SINTESE_DE_FRASE = 12000

// [spec-008 T023] Prazo da ABERTURA da sessão em tempo real. Maior que o da
// síntese de frase de propósito: esta requisição não é uma chamada ao
// provedor, são DUAS coisas em série no servidor — criar a sessão no provedor
// (`POST /v1/live/sessions`) e trocar o offer do navegador por um answer
// (`core/voz_tempo_real_openai.py::abrir_sessao`) — mais a gravação da posse
// no Redis. Medido no G-11 (`verdicts/g11-client-delegation-real.md`, §0): do
// `POST` ao data channel aberto passaram ~6,5 s, com ICE e DTLS incluídos.
//
// ⚠️ O que este prazo NÃO faz, e é importante: estourar aqui **não cancela**
// a sessão do lado do provedor. Se o servidor já criou a sessão e a resposta
// se perdeu, existe uma sessão paga órfã — quem a recolhe é o TTL do registro
// de posse, nunca este `timeout`. É por isso que ele é generoso: desistir
// cedo demais transforma lentidão em custo sem contrapartida.
const PRAZO_MS_ABERTURA_DE_SESSAO = 20000

// Prazo do ENCERRAMENTO — curto de propósito, pelo motivo oposto. Quando esta
// chamada sai, a conexão de mídia já morreu e ninguém está esperando na tela;
// ela só registra o consumo para CONFERÊNCIA contra a fatura do provedor
// (US-6 AC2 — `fora_do_ledger: true`, nunca cobrança autoritativa). Pendurar
// aqui seguraria o encerramento da sessão de voz (e o `beforeunload`, quando
// for o caso) por nada.
const PRAZO_MS_ENCERRAMENTO_DE_SESSAO = 8000

// Clone profundo do envelope cacheado — quem ordenar/filtrar `r.value` in-place
// não pode mudar o catálogo dos outros consumidores.
function clonarEnvelope(r) {
  return JSON.parse(JSON.stringify(r))
}

/**
 * Entrada e saída de voz do composer do Chat IA (v1, por turnos).
 *
 * `transcrever` (Ditar/Modo de voz) e `sintetizar` (resposta falada da IA) —
 * as duas únicas peças de voz do módulo. O áudio gravado é DESCARTADO no
 * backend depois de transcrito/sintetizado: nada persiste além do texto,
 * como se tivesse sido digitado (documentado também em `usecase_voz_stt.py`/
 * `usecase_voz_tts.py`, no backend).
 *
 * `catalogo` e `amostra` são do catálogo curado de vozes (spec-016,
 * [US-2]/[US-3]) — "escolher a voz da IA no Modo de Voz".
 */
class APIVoz extends APITemplate {
  constructor() {
    super()
    // Cache do catálogo (spec-016, [US-2]) — em memória, por carregamento de
    // página. A lista é curada e ESTÁTICA de propósito (não muda no meio de uma
    // sessão), e o composer tem DUAS instâncias mutuamente exclusivas (herói e
    // rodapé, ver `motor-de-voz.js`) que montam/desmontam a cada troca de estado
    // da conversa — sem cache, cada montagem repetiria o `GET /voz/vozes`. Só
    // respostas de SUCESSO entram no cache; uma falha de rede não trava a
    // próxima tentativa. Estado de INSTÂNCIA, não de módulo.
    this._catalogoCache = null
  }

  /**
   * @param {Object} opts
   * @param {Blob} opts.audio Blob gravado pelo `GravadorDeTurno`
   *   (`utils/captura-de-voz.js`). Vai como `multipart/form-data` — o
   *   `connection.js` converte automaticamente, nunca monte FormData na mão.
   * @param {string} [opts.contentType] MIME declarado pelo gravador
   *   (`audio/webm;codecs=opus` ou `audio/mp4`), nunca inferido aqui.
   * @returns {Promise<Object>} `{status, message, value: {texto}}`
   */
  async transcrever({ audio, contentType } = {}) {
    if (!audio) {
      return this.buildErrorResponse({
        message: MSG_ERRO_TRANSCRICAO,
        value: null,
        status: 400,
      })
    }
    const r = await con.post(intelligenceV2.vozTranscrever, {
      audio,
      content_type: contentType || audio.type || '',
    })
    return (
      r ||
      this.buildErrorResponse({ message: MSG_ERRO_TRANSCRICAO, value: null })
    )
  }

  /**
   * @param {Object} opts
   * @param {string} opts.texto Texto completo da última resposta da IA.
   * @param {string} [opts.voice_id] Voz escolhida para ESTA conversa
   *   (spec-016, [US-1]). **Opcional de propósito**: ausente/vazio, a chave
   *   `voice_id` NÃO entra no corpo — é o controle negativo da spec inteira
   *   (US-1 AC2, US-4 AC1). Mandar `voice_id: null`/`''` explícito NÃO é a
   *   mesma coisa que omitir: o backend loga a recusa de um valor inválido
   *   (ADR-016-01), e "nenhuma voz escolhida" não é um valor inválido.
   * @returns {Promise<Object>} `{status, message, value: {audio_base64, content_type}}`
   */
  async sintetizar({ texto, voice_id, paraAvatar } = {}) {
    const limpo = String(texto || '').trim()
    if (!limpo) {
      return this.buildErrorResponse({
        message: MSG_ERRO_SINTESE,
        value: null,
        status: 400,
      })
    }
    const corpo = { texto: limpo }
    if (voice_id) corpo.voice_id = voice_id
    // [spec-009 avatar ao vivo] PCM 16-bit 24 kHz em vez de MP3 — o único
    // formato que o `agent.speak` do provedor aceita, e que o ElevenLabs
    // entrega direto (nada é reamostrado no navegador). Só é MANDADO quando
    // é `true`: sem a chave, o corpo é byte a byte o de hoje, e o Modo de
    // Voz não muda de comportamento.
    if (paraAvatar) corpo.para_avatar = true
    // `{json: true}`: o resource do backend declara `texto` com
    // `location='json'` — sem isto o `connection.js` manda FormData (o
    // default) e o backend responde 400 "Missing required parameter in the
    // JSON body". `transcrever` acima está CERTO em manter o default: o
    // parser dele é `location='files'`/`'form'`.
    // `timeout`: [T002] sem prazo, uma síntese pendurada trava a fila de
    // fala pra sempre (ver o comentário de `PRAZO_MS_SINTESE_DE_FRASE`).
    const r = await con.post(intelligenceV2.vozSintetizar, corpo, false, {
      json: true,
      timeout: PRAZO_MS_SINTESE_DE_FRASE,
    })
    return (
      r || this.buildErrorResponse({ message: MSG_ERRO_SINTESE, value: null })
    )
  }

  /**
   * O catálogo curado de vozes (spec-016, [US-2] AC3) — lista, default e
   * frase de amostra. Fonte ÚNICA: este módulo não tem constante própria.
   *
   * @returns {Promise<Object>} `{status, message, value: {padrao, frase_de_amostra, vozes: [{id, rotulo}]}}`
   */
  async catalogo() {
    if (this._catalogoCache) return clonarEnvelope(this._catalogoCache)
    const r = await con.get(intelligenceV2.vozCatalogo)
    if (r && r.status === 200 && r.value) {
      this._catalogoCache = clonarEnvelope(r)
      return r
    }
    return r || this.buildErrorResponse({ message: MSG_ERRO_CATALOGO, value: null })
  }

  /**
   * Sintetiza a FRASE FIXA do catálogo numa voz — "ouvir antes de fixar"
   * (spec-016, [US-3]). NUNCA aceita texto: a frase é do servidor.
   *
   * @param {Object} opts
   * @param {string} opts.voice_id
   * @returns {Promise<Object>} `{status, message, value: {audio_base64, content_type}}`
   */
  async amostra({ voice_id } = {}) {
    const id = String(voice_id || '').trim()
    if (!id) {
      return this.buildErrorResponse({
        message: MSG_ERRO_AMOSTRA,
        value: null,
        status: 400,
      })
    }
    // `timeout`: [T002] mesmo prazo de `sintetizar` — AC2, "vale para as duas
    // chamadas que sintetizam".
    const r = await con.post(
      intelligenceV2.vozAmostra,
      { voice_id: id },
      false,
      { json: true, timeout: PRAZO_MS_SINTESE_DE_FRASE }
    )
    return (
      r || this.buildErrorResponse({ message: MSG_ERRO_AMOSTRA, value: null })
    )
  }

  /**
   * Abre a sessão de voz em TEMPO REAL — [spec-008 T023], US-2.
   *
   * O navegador gera o offer WebRTC e manda só isso; o servidor cria a sessão
   * no provedor e devolve o answer. **Nenhuma credencial volta para cá**
   * (invariante N1 — `contracts/api.yaml` v2.0.0 substituiu o desenho de
   * "credencial efêmera" justamente por isto: com a troca de SDP no servidor,
   * N1 passa a ser ESTRUTURAL).
   *
   * `id_escritorio`/`id_profissional` NÃO são parâmetros e nunca serão: o
   * servidor os lê da sessão Flask e ignora qualquer valor no corpo (N2,
   * `resource_voz_tempo_real.py`). Mandá-los daqui seria sugerir que o
   * navegador escolhe o tenant.
   *
   * @param {Object} opts
   * @param {string} opts.sdpOffer O offer de `RTCPeerConnection.createOffer()`
   *   já aplicado em `setLocalDescription` — vai como `sdp_offer`.
   * @param {string} opts.superficie Qual das três superfícies do composer
   *   pediu (`ia_operacional` | `consultor_estrategico` | `fee_fixo`). O
   *   vocabulário FECHADO vive no servidor (`SUPERFICIES_CONHECIDAS`, com
   *   `choices` no `reqparse`) e **não é duplicado aqui**: valor fora da lista
   *   volta 400 nomeado, que é melhor do que duas listas divergindo em
   *   silêncio. Quem sabe qual superfície está na tela é o chamador.
   * @param {string} [opts.idConversa] Conversa a que a sessão pertence.
   *   **Opcional de propósito, e omitir ≠ mandar `null`** (mesmo racional do
   *   `voice_id` em `sintetizar`): sem conversa selecionada a chave nem entra
   *   no corpo.
   * @returns {Promise<Object>} `{status, message, value: {id_sessao,
   *   sdp_answer, modelo, teto_segundos, somente_leitura}}`
   */
  async abrirSessaoTempoReal({ sdpOffer, superficie, idConversa } = {}) {
    // ⚠️ O offer vai INTEIRO, sem `trim()` — diferente de todo outro texto
    // deste arquivo. SDP é formato de LINHAS terminadas em CRLF (RFC 4566), e
    // `RTCPeerConnection.localDescription.sdp` sempre termina em `\r\n`;
    // recortar as pontas arranca o terminador da última linha (`a=...` fica
    // sem CRLF) e o parser do outro lado pode recusar o corpo inteiro. O
    // `trim()` existe SÓ para decidir se o offer está vazio.
    const offer = String(sdpOffer || '')
    const qualSuperficie = String(superficie || '').trim()
    // Recusa ANTES da rede: sem offer não há o que negociar, e sem superfície
    // o servidor não tem como aplicar o gate do piloto (N13). Gastar o
    // round-trip para receber um 400 previsível é gastar uma sessão que pode
    // acabar criada no provedor (ver `PRAZO_MS_ABERTURA_DE_SESSAO`).
    if (!offer.trim() || !qualSuperficie) {
      return this.buildErrorResponse({
        message: MSG_ERRO_ABRIR_SESSAO,
        value: null,
        status: 400,
      })
    }
    const corpo = { sdp_offer: offer, superficie: qualSuperficie }
    if (idConversa) corpo.id_conversa = String(idConversa)
    // `{json: true}` é OBRIGATÓRIO: o `reqparse` do resource declara os três
    // campos com `location='json'`. Sem isso o `connection.js` manda
    // FormData (o default) e o backend responde "Missing required parameter
    // in the JSON body" — o mesmo bug real que `sintetizar` documenta acima.
    const r = await con.post(intelligenceV2.vozSessaoTempoReal, corpo, false, {
      json: true,
      timeout: PRAZO_MS_ABERTURA_DE_SESSAO,
    })
    return (
      r ||
      this.buildErrorResponse({ message: MSG_ERRO_ABRIR_SESSAO, value: null })
    )
  }

  /**
   * Encerra a sessão em tempo real e registra o consumo — [spec-008 T023],
   * US-2/US-6. Idempotente no servidor: encerrar duas vezes devolve o MESMO
   * total, nunca soma.
   *
   * ★ `segundosCobraveis` não está no `requestBody` do `contracts/api.yaml`,
   * mas o servidor o exige (`_request_parser_encerramento`, `required=True`) —
   * e a razão está na D-1: a conexão de mídia mora no NAVEGADOR, o backend
   * nunca observa a sessão WebRTC. Só quem mediu pode reportar quanto durou.
   * Divergência entre contrato e implementação registrada aqui em vez de
   * descoberta no primeiro 400 de produção.
   *
   * O servidor recusa `null`/`undefined`/booleano/fracionário com o valor
   * ofensor na mensagem. Este método **normaliza** o número antes de enviar
   * (o navegador mede em ms fracionários) em vez de deixar cada chamador
   * lembrar de arredondar — mas recusa o que não é número, porque
   * `NaN`/`Infinity` viram `null` no `JSON.stringify` e chegariam lá como
   * "consumo desconhecido" disfarçado de zero.
   *
   * @param {Object} opts
   * @param {string} opts.idSessao O `id_sessao` devolvido pela abertura.
   * @param {number} opts.segundosCobraveis Duração medida pelo navegador —
   *   preferencialmente o ledger nativo do provedor (`session.usage.updated`,
   *   G-11/G-13), não o relógio de parede.
   * @returns {Promise<Object>} `{status, message, value: {id_sessao,
   *   segundos_cobraveis, fora_do_ledger}}`
   */
  async encerrarSessaoTempoReal({ idSessao, segundosCobraveis } = {}) {
    const guarda = requireId(idSessao)
    if (guarda) return guarda
    if (
      typeof segundosCobraveis !== 'number' ||
      !Number.isFinite(segundosCobraveis)
    ) {
      return this.buildErrorResponse({
        message: MSG_SEGUNDOS_INVALIDOS,
        value: null,
        status: 400,
      })
    }
    const segundos = Math.max(0, Math.round(segundosCobraveis))
    const r = await con.post(
      intelligenceV2.vozSessaoTempoRealEncerrar(idSessao),
      { segundos_cobraveis: segundos },
      false,
      { json: true, timeout: PRAZO_MS_ENCERRAMENTO_DE_SESSAO }
    )
    return (
      r ||
      this.buildErrorResponse({ message: MSG_ERRO_ENCERRAR_SESSAO, value: null })
    )
  }
}

export const apiVoz = new APIVoz()
export default apiVoz
export {
  PRAZO_MS_SINTESE_DE_FRASE,
  PRAZO_MS_ABERTURA_DE_SESSAO,
  PRAZO_MS_ENCERRAMENTO_DE_SESSAO,
}
